import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Share } from 'lucide-react';

import {
  canInstall, subscribeInstall, triggerInstall, isStandalone, isIOS,
} from '@/utils/pwaInstall';

/**
 * "Installer l'application" button — installs Inside Panama as a desktop /
 * mobile app (PWA) so it lives on the user's desktop, Start menu, dock or
 * home screen in its own standalone window. Inspired by the "download the
 * app" affordance of platforms like The Real World, but native to the web
 * (no store, no download — one click).
 *
 * Behaviour :
 *   - Chromium (Chrome / Edge / Brave, desktop + Android) → fires the native
 *     install prompt.
 *   - iOS Safari (no programmatic prompt) → reveals a manual hint
 *     (Share → Add to Home Screen).
 *   - Already installed (standalone) → renders nothing.
 *   - Browser with no install path (Firefox / desktop Safari) → renders
 *     nothing (no dead button).
 *
 * @param {string} [variant='solid']  'solid' (bordeaux CTA) | 'ghost'
 */
const T = {
  install: { fr: "Installer l'application", en: 'Install the app', es: 'Instalar la app' },
  iosHint: {
    fr: "Sur iPhone : appuie sur Partager, puis « Sur l'écran d'accueil ».",
    en: 'On iPhone: tap Share, then “Add to Home Screen”.',
    es: 'En iPhone: pulsa Compartir y luego “Añadir a pantalla de inicio”.',
  },
};

export default function InstallAppButton({ className = '', variant = 'solid' }) {
  const { i18n } = useTranslation();
  const lang = ['fr', 'en', 'es'].includes(i18n.language) ? i18n.language : 'fr';
  const [available, setAvailable] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    setAvailable(canInstall());
    setInstalled(isStandalone());
    const unsub = subscribeInstall((v) => setAvailable(v));
    const onInstalled = () => setInstalled(true);
    window.addEventListener('appinstalled', onInstalled);
    return () => { unsub(); window.removeEventListener('appinstalled', onInstalled); };
  }, []);

  if (installed) return null;
  if (!available && !ios) return null;

  const onClick = async () => {
    if (available) { await triggerInstall(); return; }
    setIosHint((v) => !v);
  };

  const btnClass = variant === 'ghost'
    ? 'inline-flex items-center gap-2 rounded-btn border border-navy-900/20 px-3.5 py-2 text-sm font-semibold text-navy-900 transition-colors hover:bg-navy-900/5 dark:border-white/25 dark:text-white dark:hover:bg-white/10'
    : 'inline-flex items-center gap-2 rounded-btn bg-bordeaux-600 px-4 py-2 text-sm font-semibold text-cream-50 shadow-card transition-transform hover:-translate-y-0.5 hover:bg-bordeaux-700';

  return (
    <div className={className}>
      <button type="button" onClick={onClick} className={btnClass}>
        <Download size={16} aria-hidden="true" />
        {T.install[lang]}
      </button>
      {iosHint && (
        <p className="mt-2 flex items-start gap-1.5 text-xs leading-snug text-gray-700 dark:text-white/70">
          <Share size={13} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>{T.iosHint[lang]}</span>
        </p>
      )}
    </div>
  );
}
